import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Users, Loader2 } from 'lucide-react';

/**
 * JoinPage - Handles direct invitation links from emails
 * URL format: /join?code=INV-XXXXXX-XXXXXXXX
 * 
 * If user is authenticated: redirects to onboarding with code pre-filled
 * If user is not authenticated: redirects to landing page, stores code in sessionStorage
 */
export default function JoinPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  const { language, t } = useLanguage();

  useEffect(() => {
    const code = searchParams.get('code');
    
    if (!code) {
      // No code provided, redirect to home
      navigate('/');
      return;
    }

    // Wait for auth to be determined
    if (authLoading) {
      return;
    }

    // Store the code for later use
    sessionStorage.setItem('spenspot_invite_code', code.toUpperCase());

    if (isAuthenticated) {
      // User is logged in
      if (user?.onboarding_completed && !user?.family_id) {
        // User completed onboarding but has no family - go to family page
        navigate('/family', { state: { inviteCode: code.toUpperCase() } });
      } else if (!user?.onboarding_completed) {
        // User hasn't completed onboarding - go there with code
        navigate('/onboarding', { state: { inviteCode: code.toUpperCase() } });
      } else {
        // User already has a family
        navigate('/family');
      }
    } else {
      // User not logged in - redirect to landing page
      // The code is stored in sessionStorage and will be used after login
      navigate('/', { state: { showInviteFlow: true, inviteCode: code.toUpperCase() } });
    }
  }, [searchParams, navigate, user, authLoading, isAuthenticated]);

  // Show loading state while processing
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center p-4">
      <div className="text-center">
        <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
          <Users className="w-10 h-10 text-primary" />
        </div>
        <h1 className="text-2xl font-bold mb-2">
          {language === 'fr' ? 'Invitation en cours...' : language === 'pt' ? 'Processando convite...' : 'Processing invitation...'}
        </h1>
        <p className="text-muted-foreground mb-6">
          {language === 'fr' 
            ? 'Veuillez patienter...'
            : language === 'pt'
            ? 'Por favor, aguarde...'
            : 'Please wait...'}
        </p>
        <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
      </div>
    </div>
  );
}
